// apps/spa/src/components/reminders/DmTestButton.tsx
// "Test to my DMs" button in the ReminderEditorForm toolbar.
//
// Uses useFormContext to read current form values, builds the embed wire shape
// (same boundary translation as onSubmit: hex→int color, strip empty fields),
// then calls useDmTestReminder mutation.
//
// Disabled when: form is invalid OR mutation is pending.
//
// Toast behavior:
//   success          → toast '✓ Sent to your DMs — check Discord.'
//   dm_closed        → toast.warning with help link to Discord DM settings article
//   other failure    → toast.error 'DM test failed: <message>'
//
// The "How? ↗" link opens the Discord support article on enabling DMs from server members.

import { useFormContext } from 'react-hook-form';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { hexToInt, isValidHex } from '@/lib/color';
import { useDmTestReminder, type DmTestResult } from '@/hooks/useDmTestReminder';
import type { ReminderFormValues } from './ReminderEditorForm';

const DM_HELP_URL = 'https://support.discord.com/hc/en-us/articles/217916488';

interface DmTestButtonProps {
  reminderId?: string;
}

export function DmTestButton({ reminderId }: DmTestButtonProps) {
  const form = useFormContext<ReminderFormValues>();
  const mutation = useDmTestReminder();

  function buildEmbed(): object {
    const values = form.getValues();
    const colorHex = values.embed.color;
    const color =
      colorHex && isValidHex(colorHex) ? hexToInt(colorHex) : undefined;

    const embedObj: Record<string, unknown> = { ...values.embed, color };
    if (color === undefined) delete embedObj.color;
    if (!embedObj.title) delete embedObj.title;
    if (!embedObj.description) delete embedObj.description;
    if (!embedObj.url) delete embedObj.url;
    const footer = embedObj.footer as { text?: string; icon_url?: string } | undefined;
    if (!footer?.text) {
      delete embedObj.footer;
    } else if (!footer.icon_url) {
      delete (footer as { icon_url?: string }).icon_url;
    }
    const thumbnail = embedObj.thumbnail as { url?: string } | undefined;
    if (!thumbnail?.url) delete embedObj.thumbnail;
    const image = embedObj.image as { url?: string } | undefined;
    if (!image?.url) delete embedObj.image;

    return embedObj;
  }

  function handleClick() {
    const embed = buildEmbed();
    mutation.mutate(
      { reminderId, embed },
      {
        onSuccess: (result: DmTestResult) => {
          if (result.success) {
            toast.success('✓ Sent to your DMs — check Discord.');
          } else if (result.error === 'dm_closed') {
            toast.warning(
              <span>
                Couldn&apos;t DM you — enable DMs from BASTN members in Discord settings.{' '}
                <a
                  href={DM_HELP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline"
                >
                  How? ↗
                </a>
              </span>,
            );
          } else {
            toast.error(`DM test failed: ${result.message}`);
          }
        },
        onError: () => {
          toast.error('DM test failed. Try again.');
        },
      },
    );
  }

  return (
    <Button
      type="button"
      variant="outline"
      disabled={!form.formState.isValid || mutation.isPending}
      onClick={handleClick}
    >
      Test to my DMs
    </Button>
  );
}
